/**
 * Website/Public JavaScript Entry Point
 * Location: resources/js/website/app.js
 */

import { createApp } from "vue";
import PrimeVue from "primevue/config";
import Aura from "@primeuix/themes/aura";
// import Swal from "sweetalert2";

// Website-specific components only
import CourseCalculatorComponent from "../components/website/institutes/courseCalculator/CourseCalculatorComponent.vue";
import StartDateComponent from "../components/website/institutes/courseCalculator/StartDateComponent.vue";
import CityComponent from "../components/website/CityComponent.vue";
import CountryComponent from "../components/website/CountryComponent.vue";
import CoursePriceInfoComponent from "../components/website/CoursePriceInfoComponent.vue";
import CourseBooking from "../components/website/institutes/courseCalculatorLegacy/CourseBooking.vue";
import InstitutesPageComponent from "../components/website/InstitutesPageComponent.vue";
import SearchComponent from "../components/website/SearchComponent.vue";
import SearchOffersComponent from "../components/website/SearchOffersComponent.vue";

const app = createApp({
    components: {
        CourseCalculatorComponent,
        CityComponent,
        CountryComponent,
        CoursePriceInfoComponent,
        CourseBooking,
        InstitutesPageComponent,
        SearchComponent,
        SearchOffersComponent,
        StartDateComponent,
    },
});

// define global methods for vue components
app.mixin({
    methods: {
        weeksOutput(weeks) {
            let msg = "";
            if (weeks === 1) msg = "اسبوع";
            else if (weeks === 2) msg = "اسبوعان";
            else if (weeks > 2 && weeks < 11) msg = weeks + " اسابيع";
            else msg = weeks + " اسبوع";
            return msg;
        },

        formatDate(date) {
            if (!date) return "";

            if (typeof date === "string") {
                return date;
            }

            if (date instanceof Date) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
            }

            return "";
        },

        setCurrentTime(value) {
            if (value) {
                const now = new Date();
                value.setHours(now.getHours());
                value.setMinutes(now.getMinutes());
                value.setSeconds(now.getSeconds());
                this.start_date = value;
            }
        },

        sweetAlertMSG(message, icon) {
            const SwalRef = window.Swal || window.Sweetalert2;
            if (SwalRef && typeof SwalRef.fire === "function") {
                SwalRef.fire({ title: message, text: "", icon: icon });
            } else {
                alert(message);
            }
        },

        formatPhoneNumber(phoneNumber, countryCode) {
            // Remove any spaces or non-numeric characters from the input
            phoneNumber = phoneNumber.replace(/[^0-9]/g, "");

            // Check if the phone number starts with a plus sign
            if (phoneNumber.charAt(0) === "+") {
                return phoneNumber;
            }

            // Add the country code if the phone number doesn't start with it already
            if (phoneNumber.substring(0, countryCode.length) !== countryCode) {
                phoneNumber = countryCode + phoneNumber;
            }

            // Add a plus sign to the beginning of the phone number
            return "+" + phoneNumber.replace('9660', '966');
        }


    },
});

// Configure PrimeVue for website
app.use(PrimeVue, {
    theme: {
        preset: Aura,
        options: {
            darkModeSelector: false,
            prefix: "p",
        },
    },
});

// Website-specific global configurations
app.config.globalProperties.$websiteConfig = {
    apiUrl: "/api",
    assetsPath: "/assets",
};

// Mount the app
app.mount(".vue_app");

// Website-specific JavaScript
document.addEventListener("DOMContentLoaded", function () {
    // Website initialization code
    console.log("Website initialized");

    // Initialize website-specific features
    initializeWebsiteFeatures();

    // Performance optimizations for website
    lazyLoadImages();
    setupIntersectionObserver();
});

function initializeWebsiteFeatures() {
    // Website-specific feature initialization
    // Initialize carousels, modals, etc.
}

function lazyLoadImages() {
    // Implement lazy loading for better performance
    const images = document.querySelectorAll("img[data-src]");
    const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const img = entry.target;
                img.src = img.dataset.src;
                img.classList.remove("lazy");
                imageObserver.unobserve(img);
            }
        });
    });

    images.forEach((img) => imageObserver.observe(img));
}

function setupIntersectionObserver() {
    // Setup observers for animations, analytics, etc.
}

import "./bootstrap"; // Website-specific bootstrap

import "../../../public/website/js/plugins/owl.carousel.min.js";
import "../../../public/website/js/sweetalert.js";
import "../../../public/website/js/plugins/uploadImg.js";
import "../../../public/website/js/custom.js";

// Ensure Swal is globally available (UMD bundle may only set window.Sweetalert2)
if (typeof window !== "undefined" && typeof window.Swal === "undefined" && typeof window.Sweetalert2 !== "undefined") {
    window.Swal = window.Sweetalert2;
}

(function () {
    "use strict";

    // Your SVG template
    const SAR_SVG = `<svg width="15px" class="sar-icon" style="display: inline-block; vertical-align: middle; margin: 0 2px;" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1124.14 1256.39">
        <path fill="currentColor" d="M699.62,1113.02h0c-20.06,44.48-33.32,92.75-38.4,143.37l424.51-90.24c20.06-44.47,33.31-92.75,38.4-143.37l-424.51,90.24Z"/>
        <path fill="currentColor" d="M1085.73,895.8c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.33v-135.2l292.27-62.11c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.27V66.13c-50.67,28.45-95.67,66.32-132.25,110.99v403.35l-132.25,28.11V0c-50.67,28.44-95.67,66.32-132.25,110.99v525.69l-295.91,62.88c-20.06,44.47-33.33,92.75-38.42,143.37l334.33-71.05v170.26l-358.3,76.14c-20.06,44.47-33.32,92.75-38.4,143.37l375.04-79.7c30.53-6.35,56.77-24.4,73.83-49.24l68.78-101.97v-.02c7.14-10.55,11.3-23.27,11.3-36.97v-149.98l132.25-28.11v270.4l424.53-90.28Z"/>
    </svg>`;

    // Global SAR replacer object
    window.SARReplacer = {
        processedNodes: new WeakSet(),

        // Replace SAR with SVG in any element
        replace: function (element = document.body) {
            const walker = document.createTreeWalker(
                element,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode: function (node) {
                        const parent = node.parentNode;
                        if (
                            !parent ||
                            ["SCRIPT", "STYLE", "SVG"].includes(parent.tagName)
                        ) {
                            return NodeFilter.FILTER_REJECT;
                        }

                        // Skip already processed nodes
                        if (
                            this.processedNodes &&
                            this.processedNodes.has(node)
                        ) {
                            return NodeFilter.FILTER_REJECT;
                        }

                        return node.textContent.includes("SAR")
                            ? NodeFilter.FILTER_ACCEPT
                            : NodeFilter.FILTER_REJECT;
                    }.bind(this),
                }
            );

            const textNodes = [];
            let node;
            while ((node = walker.nextNode())) {
                textNodes.push(node);
            }

            textNodes.forEach((textNode) => {
                if (
                    textNode.textContent.includes("SAR") &&
                    !this.processedNodes.has(textNode)
                ) {
                    const parent = textNode.parentNode;
                    const fragment = document.createDocumentFragment();

                    const parts = textNode.textContent.split(/\b(SAR)\b/);

                    parts.forEach((part) => {
                        if (part === "SAR") {
                            const tempDiv = document.createElement("div");
                            tempDiv.innerHTML = SAR_SVG;
                            fragment.appendChild(tempDiv.firstChild);
                        } else if (part) {
                            const newTextNode = document.createTextNode(part);
                            this.processedNodes.add(newTextNode);
                            fragment.appendChild(newTextNode);
                        }
                    });

                    parent.insertBefore(fragment, textNode);
                    parent.removeChild(textNode);
                }
            });
        },

        // Process text string and return HTML
        processText: function (text) {
            if (typeof text !== "string") return text;
            return text.replace(/\bSAR\b/g, SAR_SVG);
        },

        // Force replacement with a small delay to handle Vue updates
        forceReplace: function (element) {
            // Clear processed nodes to allow re-processing
            this.processedNodes = new WeakSet();

            // Use requestAnimationFrame to ensure DOM is updated
            requestAnimationFrame(() => {
                this.replace(element);
            });
        },

        // Initialize auto-replacement
        init: function () {
            // Replace existing content
            this.replace();

            // Watch for new content with improved mutation observer
            if (typeof MutationObserver !== "undefined") {
                const observer = new MutationObserver((mutations) => {
                    let shouldReplace = false;

                    mutations.forEach((mutation) => {
                        if (mutation.type === "childList") {
                            mutation.addedNodes.forEach((node) => {
                                if (
                                    node.nodeType === Node.ELEMENT_NODE ||
                                    (node.nodeType === Node.TEXT_NODE &&
                                        node.textContent.includes("SAR"))
                                ) {
                                    shouldReplace = true;
                                }
                            });
                        }

                        if (
                            mutation.type === "characterData" &&
                            mutation.target.textContent.includes("SAR")
                        ) {
                            shouldReplace = true;
                        }
                    });

                    if (shouldReplace) {
                        this.forceReplace();
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                    characterData: true,
                });
            }
        },
    };

    // Auto-initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
            SARReplacer.init();
        });
    } else {
        SARReplacer.init();
    }

    // Vue.js integration with improved handling
    if (typeof window.Vue !== "undefined") {
        // Vue 2
        window.Vue.mixin({
            methods: {
                $replaceSAR: function (text) {
                    return window.SARReplacer.processText(text);
                },
            },
            updated: function () {
                // Force replacement on every update
                this.$nextTick(() => {
                    window.SARReplacer.forceReplace(this.$el);
                });
            },
            mounted: function () {
                this.$nextTick(() => {
                    window.SARReplacer.forceReplace(this.$el);
                });
            },
        });
    }

    // For Vue 3 (if using)
    if (typeof window.app !== "undefined" && window.app.config) {
        window.app.config.globalProperties.$replaceSAR = function (text) {
            return window.SARReplacer.processText(text);
        };
    }
})();
